import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { PaperClipIcon, ChatBubbleLeftIcon, CheckCircleIcon, ArrowLeftIcon, PencilIcon, TrashIcon, LinkIcon, UserPlusIcon, XMarkIcon, UserGroupIcon, CheckIcon } from '@heroicons/react/24/outline';
import UnifiedTimeline from '../components/UnifiedTimeline';
import sanitizeHtml from '../utils/sanitizeHtml';
import ScrollableDropdown from '../components/ScrollableDropdown';
import SmartBugLink from '../components/SmartBugLink';
import MediaViewer, { getMediaKind } from '../components/MediaViewer';

const statusColors = {
  'Open': 'bg-blue-50 text-blue-900 border border-blue-100',
  'In Progress': 'bg-orange-50 text-orange-900 border border-orange-100',
  'Fixed': 'bg-emerald-50 text-emerald-900 border border-emerald-100',
  'Retesting In Progress': 'bg-indigo-50 text-indigo-900 border border-indigo-100',
  'Closed': 'bg-green-100 text-green-950 border border-green-200',
  'Reopen': 'bg-red-50 text-red-950 border border-red-100',
  'Recurring Issue': 'bg-rose-50 text-rose-950 border border-rose-100',
  'On Hold': 'bg-yellow-50 text-yellow-950 border border-yellow-100',
  'Duplicate': 'bg-purple-50 text-purple-950 border border-purple-100',
  'Rejected': 'bg-gray-100 text-gray-950 border border-gray-200',
  'Not a Bug': 'bg-slate-100 text-slate-950 border border-slate-200',
  'Deferred': 'bg-amber-50 text-amber-950 border border-amber-100',
  'Deprecated': 'bg-gray-200 text-gray-950 border border-gray-300'
};

const statusIndicatorColors = {
  'Open': '#2563EB',
  'In Progress': '#EA580C',
  'Fixed': '#10B981',
  'Retesting In Progress': '#4F46E5',
  'Closed': '#166534',
  'Reopen': '#DC2626',
  'Recurring Issue': '#BE123C',
  'On Hold': '#D97706',
  'Duplicate': '#9333EA',
  'Rejected': '#374151',
  'Not a Bug': '#475569',
  'Deferred': '#B45309',
  'Deprecated': '#1F2937'
};

const displayStatus = (s) => (s === 'Retesting In Progress' ? 'QA In Progress' : (s === 'In Progress' ? 'Dev In Progress' : s));

const severityColors = {
  'Low': 'bg-[#E3FCEF] text-[#006644]',
  'Medium': 'bg-[#FFF0B3] text-[#172B4D]',
  'High': 'bg-[#FFEBE6] text-[#DE350B]',
  'Critical': 'bg-[#FFEBE6] text-[#BF2600]'
};

const priorityColors = {
  'Low': 'bg-[#E3FCEF] text-[#006644]',
  'Medium': 'bg-[#FFF0B3] text-[#172B4D]',
  'High': 'bg-[#FFEBE6] text-[#DE350B]',
  'Critical': 'bg-[#FFEBE6] text-[#BF2600]'
};

const getAttachmentUrl = (url) => {
  if (!url) return '#';
  if (url.startsWith('http')) return url;
  const baseUrl = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'development' ? 'http://localhost:5000' : '');
  return `${baseUrl}${url.startsWith('/') ? '' : '/'}${url}`;
};

const BugDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [bug, setBug] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState('');
  const [editingComment, setEditingComment] = useState(null);
  const [editedCommentText, setEditedCommentText] = useState('');
  const [highlightCommentId, setHighlightCommentId] = useState(null);
  const [activeMediaIndex, setActiveMediaIndex] = useState(0);
  const mediaViewerRef = useRef(null);
  const [updating, setUpdating] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [replyingToComment, setReplyingToComment] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [editingReplyId, setEditingReplyId] = useState(null);
  const [editedReplyText, setEditedReplyText] = useState('');
  const [activeInputType, setActiveInputType] = useState('comment');
  const replyInputRef = useRef(null);

  // Close Bug Modal state
  const [showCloseBugModal, setShowCloseBugModal] = useState(false);
  const [pendingVerificationStatus, setPendingVerificationStatus] = useState('Closed');
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [closeBugVerificationLink, setCloseBugVerificationLink] = useState('');
  const [closeBugVerificationNotes, setCloseBugVerificationNotes] = useState('');

  // Placeholder removed as RoleIcon was unused

  // Mention & Suggestion states
  const [allUsers, setAllUsers] = useState([]);
  const [myBugs, setMyBugs] = useState([]);
  const [mentionQuery, setMentionQuery] = useState(null);
  const [suggestionType, setSuggestionType] = useState(null); // 'user' or 'bug'
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [mentionPosition, setMentionPosition] = useState({ top: 0, left: 0 });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const commentInputRef = useRef(null);
  const mirrorRef = useRef(null);

  const parseDescriptionIntoSections = (html) => {
    // 1. Pre-normalize: Convert &nbsp; to space to ensure regex matches text correctly
    const input = html == null ? '' : String(html).replace(/&nbsp;/gi, ' ');
    const empty = { description: input, steps: '', expected: '', actual: '', hasHeadings: false };
    if (!input) return { description: '', steps: '', expected: '', actual: '', hasHeadings: false };

    // Unique delimiter for splitting sections
    const DELIMITER = '<!--___SECTION_SPLIT___:';
    const SUFFIX = '-->';

    const normalize = (s) =>
      String(s || '')
        .replace(/\u00a0/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase()
        .replace(/^[•\-*]\s*/, '') // Remove leading bullets/dashes
        .replace(/:$/, ''); // Remove trailing colon

    const toKey = (s) => {
      const t = normalize(s);

      // Description variations
      if (t === 'description' ||
        t === 'defect description' ||
        t === 'bug description' ||
        t === 'issue description' ||
        t === 'problem description') {
        return 'description';
      }

      // Steps to Reproduce variations
      if (t === 'steps to reproduce' ||
        t === 'steps' ||
        t === 'reproduction steps' ||
        t === 'repro steps' ||
        t === 'how to reproduce' ||
        t === 'reproduce steps') {
        return 'steps';
      }

      // Expected Result variations (including plural)
      if (t === 'expected result' ||
        t === 'expected results' ||
        t === 'expected behavior' ||
        t === 'expected outcome' ||
        t === 'expected') {
        return 'expected';
      }

      // Actual Result variations (including plural)
      if (t === 'actual result' ||
        t === 'actual results' ||
        t === 'actual behavior' ||
        t === 'actual outcome' ||
        t === 'actual') {
        return 'actual';
      }

      return null;
    };

    // Regex to match heading text variants
    // Matches: defect description, steps to reproduce, etc.
    const headingTextPattern = '(?:defect\\s+description|bug\\s+description|issue\\s+description|problem\\s+description|description|steps\\s*to\\s*reproduce|reproduction\\s+steps|repro\\s+steps|how\\s+to\\s+reproduce|reproduce\\s+steps|steps|expected\\s*results?|expected\\s+behavior|expected\\s+outcome|expected|actual\\s*results?|actual\\s+behavior|actual\\s+outcome|actual)';

    // 1. Replace Styles/Bold/Span headings with markers
    // Matches: <strong class="...">Heading:</strong> or <span style="...">Heading</span>
    // Uses backreference \1 to match the closing tag correctly
    const tagRegex = new RegExp(`<(strong|b|span|u|em)(?:\\s+[^>]*)?>\\s*([•\\-*]?\\s*${headingTextPattern}\\s*:?)\\s*<\\/\\1>`, 'gi');

    let processed = input.replace(tagRegex, (match, tag, text) => {
      const key = toKey(text);
      return key ? `${DELIMITER}${key}${SUFFIX}` : match;
    });

    // 2. Replace PLAIN headings with markers
    // Matches start of line or block boundaries
    const plainRegex = new RegExp(`(?:^|>|[\\r\\n])\\s*([•\\-*]?\\s*${headingTextPattern}\\s*:?)\\s*(?=$|<|[\\r\\n])`, 'gi');

    processed = processed.replace(plainRegex, (match, headingGroup, offset) => {
      const key = toKey(headingGroup);
      if (!key) return match;

      const idx = match.indexOf(headingGroup);
      const pre = match.substring(0, idx);
      const post = match.substring(idx + headingGroup.length);

      return `${pre}${DELIMITER}${key}${SUFFIX}${post}`;
    });

    // 3. Split by delimiter
    const parts = processed.split(DELIMITER);
    const out = { description: '', steps: '', expected: '', actual: '', hasHeadings: false };

    // If the split didn't find anything, fallback 
    if (parts.length === 1 && parts[0] === processed) {
      return empty;
    }

    // Assign chunks
    parts.forEach((part, index) => {
      // First chunk is content before the first heading
      if (index === 0) {
        if (part.trim()) {
          out.description = part;
        }
        return;
      }

      // Other chunks start with KEY + SUFFIX
      const suffixIdx = part.indexOf(SUFFIX);
      if (suffixIdx !== -1) {
        const key = part.substring(0, suffixIdx);
        const content = part.substring(suffixIdx + SUFFIX.length);

        // Soft detection logic: Only assign if content is non-trivial (more than just a few characters/tags)
        // This helps avoid "empty or incorrect sections"
        const cleanContent = content.replace(/<[^>]*>/g, '').trim();
        if (out.hasOwnProperty(key) && cleanContent.length > 2) {
          out.hasHeadings = true;
          out[key] += content;
        } else {
          // If content is too short/empty, don't treat it as a separate section
          // Instead, keep it in the flow or discard if it was just a heading
          if (index === 1 && key === 'description') {
            // If the first section is "Description:", we can ignore the heading marker
            out.description += content;
          }
        }
      }
    });

    return out.hasHeadings ? out : empty;
  };



  // NOTE: Status dropdown logic is computed below (after `if (!bug)` guard) and rendered inline.
  // We intentionally do NOT define a StatusDropdown component inside BugDetail's render body,
  // because React would treat it as a new component type on every re-render, unmounting it
  // the instant it opens (e.g., when `updating` state changes), making it unclickable.

  const fetchBug = React.useCallback(async (showLoading = true) => {
    if (showLoading) {
      setLoading(true);
    }
    try {
      // The backend /api/bugs/:id endpoint natively resolves both:
      //   - MongoDB ObjectIds  (24-hex chars)  → searches by _id
      //   - Human-readable bug keys (BUG-236) → searches by bugId field
      // It has NO isArchived filter, so archived bugs load without issues.
      const isMongoId = /^[0-9a-fA-F]{24}$/.test(id);

      const { data } = await axios.get(`/api/bugs/${id}`);
      const bugData = data?.data ?? null;

      if (bugData) {
        setBug(bugData);

        // Industry-Standard Cleanup: If the URL uses internal ObjectId, replace it
        // with the human-readable Bug Key (e.g., /bugs/BUG-236)
        if (isMongoId && bugData.bugId) {
          navigate(`/bugs/${bugData.bugId}`, { replace: true });
        }
      } else {
        throw new Error('Bug not found');
      }
    } catch (error) {
      console.error('Error fetching bug:', error);
      if (showLoading) {
        toast.error('Failed to load bug details');
        navigate('/bugs');
      }
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchBug();
  }, [fetchBug]);

  useEffect(() => {
    const handleBugUpdated = (e) => {
      // Refresh bug details if this bug was updated
      // We check both _id and bugId to ensure we catch updates regardless of how the page was loaded
      if (e.detail?.bugId === bug?._id || e.detail?.bugId === id) {
        fetchBug(false);
      }
    };
    window.addEventListener('bugtracker:bug-updated', handleBugUpdated);
    const handleStorage = (e) => {
      if (e.key === 'bugtracker:bug-updated') {
        fetchBug(false);
      }
    };
    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener('bugtracker:bug-updated', handleBugUpdated);
      window.removeEventListener('storage', handleStorage);
    };
  }, [id, fetchBug, bug]);

  useEffect(() => {
    if (!bug) return;
    const params = new URLSearchParams(location.search || '');
    const commentId = params.get('commentId');
    if (!commentId) return;
    const el = document.getElementById(`comment-${commentId}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setHighlightCommentId(commentId);
      const t = setTimeout(() => setHighlightCommentId(null), 3000);
      return () => clearTimeout(t);
    }
  }, [bug, location.search]);

  // Auto-refresh bug details every 15 seconds for real-time updates
  useEffect(() => {
    const interval = setInterval(() => {
      fetchBug(false);
    }, 15000);
    return () => clearInterval(interval);
  }, [fetchBug]);


  const fetchAllUsers = async () => {
    try {
      const { data } = await axios.get('/api/auth/all-users');
      setAllUsers(data.data);
    } catch (error) {
      console.error('Failed to fetch all users:', error);
    }
  };

  const fetchMyBugs = async (searchQuery = '') => {
    if (!user) return;
    try {
      // If we have a search query, search across all relevant bugs
      // Otherwise, fetch recent bugs for the current context
      const params = {
        pageSize: searchQuery ? 20 : 200, // 200 for initial load, 20 for search
        search: searchQuery
      };

      if (bug?.application?._id || bug?.application) {
        params.application = bug.application._id || bug.application;
      } else {
        if (user.role === 'developer') {
          params.assignedTo = user._id || user.id;
        } else {
          params.reportedBy = user._id || user.id;
        }
      }

      const { data } = await axios.get('/api/bugs', { params });
      if (searchQuery) {
        // Merge search results with existing myBugs to maintain stability
        setMyBugs(prev => {
          const combined = [...data.data, ...prev];
          const unique = new Map();
          combined.forEach(b => unique.set(b._id, b));
          return Array.from(unique.values()).slice(0, 300);
        });
      } else {
        setMyBugs(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch bugs:', error);
    }
  };

  useEffect(() => {
    fetchAllUsers();
  }, [user]);

  useEffect(() => {
    if (user) fetchMyBugs();
  }, [user, bug?.application]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced search for bugs when query changes
  useEffect(() => {
    if (suggestionType === 'bug' && mentionQuery && mentionQuery.length >= 2) {
      const timeout = setTimeout(() => {
        fetchMyBugs(mentionQuery);
      }, 300);
      return () => clearTimeout(timeout);
    }
  }, [mentionQuery, suggestionType]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showStatusDropdown && !event.target.closest('.status-dropdown')) {
        setShowStatusDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showStatusDropdown]);

  // Custom status dropdown component
  const StatusDropdown = () => {
    const currentUserId = user._id?.toString() || user.id?.toString();
    const app = bug.application;
    const primaryDevId = app?.developerId?._id || app?.developerId;
    const ownerId = app?.ownerId?._id || app?.ownerId;

    const isOwner = ownerId && String(ownerId) === String(currentUserId);
    const isPrimaryDev = primaryDevId && String(primaryDevId) === String(currentUserId);
    const isAssignedDev =
      (bug.assignedTo?._id && String(bug.assignedTo._id) === String(currentUserId)) ||
      (bug.assignedTo && typeof bug.assignedTo === 'string' && String(bug.assignedTo) === String(currentUserId));
    const isTeamMember =
      Array.isArray(app?.teamMembers) &&
      app.teamMembers.some((m) => {
        const memberId = m.userId?._id || m.userId;
        return memberId && String(memberId) === String(currentUserId) && m.role === 'DEV';
      });

    const isAuthorizedDev = user.role === 'developer' && (isOwner || isPrimaryDev || isTeamMember || isAssignedDev);
    const isTesterOrAdmin = user.role === 'tester' || user.role === 'admin';
    const canChange = (isAuthorizedDev || isTesterOrAdmin);

    let options = [];
    if (user.role === 'developer') {
      options = [
        { value: 'In Progress', label: 'Dev In Progress', indicatorColor: statusIndicatorColors['In Progress'], description: 'Developer is actively working on this bug' },
        { value: 'Fixed', label: 'Fixed', indicatorColor: statusIndicatorColors['Fixed'], description: 'Developer has resolved the issue' },
        { value: 'On Hold', label: 'On Hold', indicatorColor: statusIndicatorColors['On Hold'], description: 'Development is on hold due to dependencies or other issues' },
        { value: 'Rejected', label: 'Rejected', indicatorColor: statusIndicatorColors['Rejected'], description: 'The bug is deemed invalid, not a real issue, or duplicate' },
        { value: 'Duplicate', label: 'Duplicate', indicatorColor: statusIndicatorColors['Duplicate'], description: 'This bug is a duplicate of another existing issue' },
        { value: 'Deferred', label: 'Deferred', indicatorColor: statusIndicatorColors['Deferred'], description: 'A bug might be postponed if the fix is too complicated or requires more information' },
        { value: 'Not a Bug', label: 'Not a Bug', indicatorColor: statusIndicatorColors['Not a Bug'], description: 'This is not a bug, but expected behavior' },
        { value: 'Deprecated', label: 'Deprecated', indicatorColor: statusIndicatorColors['Deprecated'], description: 'This bug is no longer relevant or applicable (out of scope)' }
      ];
    } else {
      // For testers or admins
      options = [
        { value: 'Open', label: 'Open', indicatorColor: statusIndicatorColors['Open'], description: 'Bug is open and ready for developer' },
        { value: 'On Hold', label: 'On Hold', indicatorColor: statusIndicatorColors['On Hold'], description: 'Bug is on hold and cannot proceed currently' },
        { value: 'Retesting In Progress', label: 'QA In Progress', indicatorColor: statusIndicatorColors['Retesting In Progress'], description: 'Tester is currently verifying the fix' },
        { value: 'Reopen', label: 'Reopen', indicatorColor: statusIndicatorColors['Reopen'], description: 'The fix failed, reopening the bug' },
        {
          value: 'Closed',
          label: 'Closed',
          indicatorColor: statusIndicatorColors['Closed'],
          disabled: (bug.status !== 'Fixed' && bug.status !== 'Retesting In Progress'),
          description: (bug.status !== 'Fixed' && bug.status !== 'Retesting In Progress') ? 'Available after developer fixes the bug' : 'Close this bug'
        },
        {
          value: 'Recurring Issue',
          label: 'Recurring Issue',
          indicatorColor: '#EF4444',
          disabled: bug.status !== 'Closed',
          description: bug.status === 'Closed' ? 'Mark as recurring — re-enters the workflow' : 'Only available for Closed bugs'
        }
      ];
    }

    // ALWAYS ensure the current bug status is in the options list, 
    // even if it's not a primary action for the user's role.
    // This prevents the dropdown from appearing empty when another user has changed the status.
    const currentStatusInOptions = options.some(opt => opt.value === bug.status);
    if (!currentStatusInOptions) {
      options.unshift({
        value: bug.status,
        label: displayStatus(bug.status),
        indicatorColor: statusIndicatorColors[bug.status],
        disabled: true,
        description: 'Status updated by another user'
      });
    }


    return (
      <div className="status-dropdown">
        <ScrollableDropdown
          value={bug.status}
          onChange={handleStatusChange}
          options={options}
          disabled={updating || !canChange}
          placeholder="Select Status"
          buttonHeightClassName="h-9"
          usePortal={true}
          buttonClassName={`font-bold transition-all border-gray-200 hover:border-gray-300 ${!canChange ? 'opacity-70 bg-gray-50' : 'bg-white'}`}
        />
        {!canChange && (
          <p className="text-[10px] text-amber-600 font-medium italic mt-1">
            {user.role === 'tester' ? 'Only testers can change this status' : 'Only application team members can change the status'}
          </p>
        )}
      </div>
    );
  };

  // eslint-disable-next-line no-unused-vars
  const downloadAttachment = async (file) => {
    try {
      const response = await axios.get(`/api/bugs/${bug._id}/attachments/${file._id}/download`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file.filename || 'download');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading attachment:', error);
      toast.error('Failed to download attachment');
    }
  };

  const handleStatusChange = async (newStatus) => {
    // Intercept 'Closed' or 'Reopen' for testers — show the verification modal first
    if ((newStatus === 'Closed' || newStatus === 'Reopen') && user?.role === 'tester') {
      setCloseBugVerificationLink('');
      setCloseBugVerificationNotes('');
      setPendingVerificationStatus(newStatus);
      setShowCloseBugModal(true);
      return;
    }
    await updateBugStatus(newStatus);
  };

  const handleCloseBugSubmit = async () => {
    const proofData = {
      proofUrl: closeBugVerificationLink.trim(),
      description: closeBugVerificationNotes.trim()
    };
    setShowCloseBugModal(false);
    await updateBugStatus(pendingVerificationStatus, proofData);
  };

  const updateBugStatus = async (status, proofData = null) => {
    setUpdating(true);
    try {
      const updateData = { status };
      if (proofData) {
        // Save each field independently — either can be provided without the other
        if (proofData.proofUrl) {
          updateData.proofUrl = proofData.proofUrl;
        }
        if (proofData.description) {
          updateData.proofDescription = proofData.description;
        }
      }

      console.log('Updating bug status:', { bugId: id, ...updateData });

      const { data } = await axios.put(`/api/bugs/${bug._id}`, updateData);

      console.log('Status update response:', data);
      setBug(data.data);

      // Dispatch event for other components to refresh (e.g., Dashboard, BugList)
      window.dispatchEvent(
        new CustomEvent('bugtracker:bug-updated', {
          detail: {
            bugId: id,
            status,
            applicationId: data.data.application?._id || data.data.application || null
          }
        })
      );

      toast.success('Status updated successfully');

      try {
        localStorage.setItem(
          'bugtracker:bug-updated',
          JSON.stringify({
            bugId: id,
            status,
            applicationId: data?.data?.application?._id || data?.data?.application || null,
            ts: Date.now()
          })
        );
      } catch (e) {
        // Ignore storage errors
      }


    } catch (error) {
      console.error('Error updating status:', error.response || error);
      if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
        toast.error('Network Error: Unable to connect to server. Please check your connection.');
      } else {
        const errorMsg = error.response?.data?.message || error.message || 'Failed to update status';
        toast.error(errorMsg);
      }
    } finally {
      setUpdating(false);
    }
  };

  const handleAssignDeveloper = async (devId) => {
    setShowAssignModal(false);
    setUpdating(true);
    try {
      const { data } = await axios.put(`/api/bugs/${bug._id}`, { assignedTo: devId });
      setBug(data.data);
      toast.success('Bug assigned successfully');
      window.dispatchEvent(
        new CustomEvent('bugtracker:bug-updated', {
          detail: {
            bugId: id,
            assignedTo: devId,
            applicationId: data.data.application?._id || data.data.application || null
          }
        })
      );
    } catch (error) {
      console.error('Error assigning bug:', error);
      toast.error(error.response?.data?.message || 'Failed to assign bug');
    } finally {
      setUpdating(false);
    }
  };

  const appDevelopers = React.useMemo(() => {
    if (!bug?.application) return [];
    const app = bug.application;
    const devs = new Map();
    const addIfDev = (u) => {
      if (!u || typeof u !== 'object') return;
      const id = u._id || u;
      if (u.role === 'developer' || u.role === 'manager') {
        devs.set(id.toString(), { _id: id.toString(), name: u.name || 'Developer', role: u.role });
      }
    };
    if (app.developerId) addIfDev(app.developerId);
    if (app.ownerId) addIfDev(app.ownerId);
    if (Array.isArray(app.teamMembers)) {
      app.teamMembers.forEach(m => {
        if (m.role === 'DEV' || m.role === 'MANAGER') addIfDev(m.userId);
      });
    }
    return Array.from(devs.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [bug]);


  const handleAddComment = async (e) => {
    if (e) e.preventDefault();
    if (!comment.trim() || updating) return;
    
    setUpdating(true);
    try {
      const { data } = await axios.post(`/api/bugs/${bug._id}/comments`, { text: comment });
      setBug(data.data);
      setComment('');
      toast.success('Comment added');
    } catch (error) {
      if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
        toast.error('Network Error: Unable to connect to server. Please check your connection.');
      } else {
        toast.error('Failed to add comment');
      }
    } finally {
      setUpdating(false);
    }
  };

  const handleAddReply = async (e, commentId) => {
    if (e) e.preventDefault();
    if (!replyText.trim() || updating) return;

    setUpdating(true);
    try {
      const { data } = await axios.post(`/api/bugs/${bug._id}/comments/${commentId}/replies`, { text: replyText });
      setBug(data.data);
      setReplyingToComment(null);
      setReplyText('');
      toast.success('Reply added');
    } catch (error) {
      if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
        toast.error('Network Error: Unable to connect to server. Please check your connection.');
      } else {
        toast.error('Failed to add reply');
      }
    } finally {
      setUpdating(false);
    }
  };

  // Helper to get coordinates for the mention dropdown
  const updateMentionPosition = (textarea, cursorIndex) => {
    if (!mirrorRef.current) return;

    const mirror = mirrorRef.current;
    const computedStyle = window.getComputedStyle(textarea);

    // Copy styles
    mirror.style.width = computedStyle.width;
    mirror.style.padding = computedStyle.padding;
    mirror.style.border = computedStyle.border;
    mirror.style.fontFamily = computedStyle.fontFamily;
    mirror.style.fontSize = computedStyle.fontSize;
    mirror.style.lineHeight = computedStyle.lineHeight;
    mirror.style.letterSpacing = computedStyle.letterSpacing;
    mirror.style.whiteSpace = 'pre-wrap';
    mirror.style.wordWrap = 'break-word';

    // Get text up to cursor
    const textBefore = textarea.value.substring(0, cursorIndex);

    // Set content. add a span at the end to track position
    mirror.textContent = textBefore;
    const span = document.createElement('span');
    span.textContent = '|';
    mirror.appendChild(span);

    // Relative position
    const top = span.offsetTop - textarea.scrollTop + 20; // +20 for line height approx
    const left = span.offsetLeft;

    setMentionPosition({ top, left });
  };

  const handleCommentChange = (e, type = 'comment') => {
    setActiveInputType(type);
    const val = e.target.value;
    const pos = e.target.selectionStart;
    
    if (type === 'reply') setReplyText(val);
    else setComment(val);

    const textBeforeCursor = val.substring(0, pos);

    // 1. Detect @mention for users
    const lastAt = textBeforeCursor.lastIndexOf('@');
    if (lastAt !== -1) {
      const charBeforeAt = lastAt > 0 ? textBeforeCursor.charAt(lastAt - 1) : '';
      const isValidStart = lastAt === 0 || /[\s([{]/.test(charBeforeAt);

      if (isValidStart) {
        const query = textBeforeCursor.substring(lastAt + 1);
        if (!query.includes(' ') && !query.includes('\n')) {
          setSuggestionType('user');
          setMentionQuery(query);
          setShowSuggestions(true);
          updateMentionPosition(e.target, lastAt + 1);
          setSelectedIndex(0);
          return;
        }
      }
    }

    // 2. Detect BUG ID (starts with 2+ letters if it looks like a prefix, or # trigger)
    const bugPrefixes = ['TID', 'APP', 'BUG'];
    let bugTriggerIdx = textBeforeCursor.lastIndexOf('#');
    let triggerLength = 1;

    if (bugTriggerIdx === -1) {
      // Check for standalone words that start with prefixes
      const words = textBeforeCursor.split(/[\s([{]/);
      const lastWord = words[words.length - 1];

      if (lastWord.length >= 2) {
        const uWord = lastWord.toUpperCase();
        const matchingPrefix = bugPrefixes.find(p => p.startsWith(uWord) || uWord.startsWith(p));
        if (matchingPrefix) {
          bugTriggerIdx = textBeforeCursor.length - lastWord.length;
          triggerLength = 0; // The word itself is the query
        }
      }
    }

    if (bugTriggerIdx !== -1) {
      const charBeforeTrigger = bugTriggerIdx > 0 ? textBeforeCursor.charAt(bugTriggerIdx - 1) : '';
      const isValidStart = bugTriggerIdx === 0 || /[\s([{]/.test(charBeforeTrigger);

      if (isValidStart) {
        const query = textBeforeCursor.substring(bugTriggerIdx + triggerLength);
        if (!query.includes(' ') && !query.includes('\n')) {
          setSuggestionType('bug');
          setMentionQuery(query);
          setShowSuggestions(true);
          updateMentionPosition(e.target, bugTriggerIdx + triggerLength);
          setSelectedIndex(0);
          return;
        }
      }
    }

    setShowSuggestions(false);
    setMentionQuery(null);
    setSuggestionType(null);
  };

  const insertMention = (item) => {
    const isReply = activeInputType === 'reply';
    const ref = isReply ? replyInputRef.current : commentInputRef.current;
    if (!ref) return;
    
    const pos = ref.selectionStart;
    const textVal = isReply ? replyText : comment;
    const textBeforeCursor = textVal.substring(0, pos);

    // Find where the trigger word/symbol started
    const bugPrefixes = ['TID', 'APP', 'BUG'];
    let lastIdx = textBeforeCursor.lastIndexOf(suggestionType === 'user' ? '@' : '#');

    if (suggestionType === 'bug' && lastIdx === -1) {
      // Look for the last word that looks like a bug prefix
      const words = textBeforeCursor.split(/[\s([{]/);
      const lastWord = words[words.length - 1];
      if (lastWord.length >= 2 && bugPrefixes.some(p => lastWord.toUpperCase().startsWith(p))) {
        lastIdx = textBeforeCursor.length - lastWord.length;
      }
    }

    if (lastIdx !== -1) {
      const prefix = textVal.substring(0, lastIdx);
      const suffix = textVal.substring(pos);

      let insertedText = '';
      let cursorOffset = 0;

      if (suggestionType === 'user') {
        insertedText = `@[${item.name}] `;
        cursorOffset = item.name.length + 4; // @ [ ] + space
      } else {
        // ALWAYS use '#' prefix for bugs so they render as SmartBugLinks
        insertedText = `#${item.bugId} `;
        cursorOffset = item.bugId.length + 2; // # + bugID + space
      }

      const newText = `${prefix}${insertedText}${suffix}`;
      
      if (isReply) setReplyText(newText);
      else setComment(newText);
      
      setShowSuggestions(false);
      setMentionQuery(null);
      setSuggestionType(null);

      setTimeout(() => {
        ref.focus();
        const newCursorPos = lastIdx + cursorOffset;
        ref.setSelectionRange(newCursorPos, newCursorPos);
      }, 0);
    }
  };

  const projectUsers = React.useMemo(() => {
    const usersMap = new Map();

    // 1. Helper to add or update a user in the map
    const addUser = (u, isPriority = false) => {
      if (!u) return;
      // Handle both populated objects and IDs
      const id = u._id || (typeof u === 'string' ? u : null);
      if (!id) return;

      const idStr = id.toString();
      const existing = usersMap.get(idStr);

      // If user doesn't exist OR we're updating a shell with a real object
      if (!existing || (!existing.isFull && u.name)) {
        usersMap.set(idStr, {
          _id: idStr,
          name: u.name || existing?.name || 'Unknown User',
          role: u.role || existing?.role || 'user',
          isPriority: isPriority || existing?.isPriority || false,
          isFull: !!u.name
        });
      }
    };

    // 2. Priority: Add everyone directly related to this bug
    if (bug) {
      if (bug.reportedBy) addUser(bug.reportedBy, true);
      if (bug.assignedTo) addUser(bug.assignedTo, true);

      if (bug.application) {
        addUser(bug.application.ownerId, true);
        addUser(bug.application.developerId, true);
        if (Array.isArray(bug.application.teamMembers)) {
          bug.application.teamMembers.forEach(m => addUser(m.userId, true));
        }
      }
    }

    // 3. Foundation: Merge with all global users
    if (Array.isArray(allUsers) && allUsers.length > 0) {
      allUsers.forEach(u => addUser(u, false));
    }

    // Convert to array and sort: priority (team) members first, then alphabetical
    return Array.from(usersMap.values()).sort((a, b) => {
      if (a.isPriority !== b.isPriority) return a.isPriority ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
  }, [bug, allUsers]);

  const filteredSuggestions = React.useMemo(() => {
    if (!showSuggestions || mentionQuery === null) return [];

    if (suggestionType === 'user') {
      if (!mentionQuery) return projectUsers.slice(0, 8);
      const lowerQ = mentionQuery.toLowerCase();
      return projectUsers
        .filter(u => u.name.toLowerCase().includes(lowerQ))
        .slice(0, 8);
    } else {
      // Bug suggestions: Sort by relevance (exact ID match > ID prefix > title match)
      const lowerQ = (mentionQuery || '').toLowerCase();
      if (!lowerQ) return myBugs.slice(0, 8);

      return myBugs
        .filter(b =>
          b.bugId.toLowerCase().includes(lowerQ) ||
          b.title.toLowerCase().includes(lowerQ)
        )
        .sort((a, b) => {
          const aId = a.bugId.toLowerCase();
          const bId = b.bugId.toLowerCase();

          // 1. Exact ID match
          if (aId === lowerQ) return -1;
          if (bId === lowerQ) return 1;

          // 2. ID prefix match
          const aStarts = aId.startsWith(lowerQ);
          const bStarts = bId.startsWith(lowerQ);
          if (aStarts && !bStarts) return -1;
          if (bStarts && !aStarts) return 1;

          return 0; // Maintain existing order for others
        })
        .slice(0, 10);
    }
  }, [showSuggestions, suggestionType, mentionQuery, projectUsers, myBugs]);

  const handleEditComment = (commentId, currentText) => {
    setEditingComment(commentId);
    setEditedCommentText(currentText);
  };

  const handleCancelEdit = () => {
    setEditingComment(null);
    setEditedCommentText('');
  };

  const handleSaveEdit = async (commentId) => {
    if (!editedCommentText.trim()) return;

    try {
      const { data } = await axios.put(`/api/bugs/${bug._id}/comments/${commentId}`, { text: editedCommentText });
      setBug(data.data);
      setEditingComment(null);
      setEditedCommentText('');
      toast.success('Comment updated');
    } catch (error) {
      if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
        toast.error('Network Error: Unable to connect to server. Please check your connection.');
      } else {
        toast.error('Failed to update comment');
      }
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (window.confirm('Are you sure you want to delete this comment?')) {
      try {
        const { data } = await axios.delete(`/api/bugs/${bug._id}/comments/${commentId}`);
        setBug(data.data);
        toast.success('Comment deleted successfully!');
      } catch (error) {
        if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
          toast.error('Network Error: Unable to connect to server. Please check your connection.');
        } else {
          toast.error(error.response?.data?.message || 'Failed to delete comment');
        }
      }
    }
  };

  const handleEditReply = (replyId, currentText) => {
    setEditingReplyId(replyId);
    setEditedReplyText(currentText);
  };

  const handleCancelEditReply = () => {
    setEditingReplyId(null);
    setEditedReplyText('');
  };

  const handleSaveEditReply = async (commentId, replyId) => {
    if (!editedReplyText.trim()) return;

    try {
      const { data } = await axios.put(`/api/bugs/${bug._id}/comments/${commentId}/replies/${replyId}`, { text: editedReplyText });
      setBug(data.data);
      setEditingReplyId(null);
      setEditedReplyText('');
      toast.success('Reply updated');
    } catch (error) {
      if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
        toast.error('Network Error: Unable to connect to server. Please check your connection.');
      } else {
        toast.error('Failed to update reply');
      }
    }
  };

  const handleDeleteReply = async (commentId, replyId) => {
    if (window.confirm('Are you sure you want to delete this reply?')) {
      try {
        const { data } = await axios.delete(`/api/bugs/${bug._id}/comments/${commentId}/replies/${replyId}`);
        setBug(data.data);
        toast.success('Reply deleted successfully!');
      } catch (error) {
        if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
          toast.error('Network Error: Unable to connect to server. Please check your connection.');
        } else {
          toast.error(error.response?.data?.message || 'Failed to delete reply');
        }
      }
    }
  };

  const renderCommentText = (text) => {
    if (!text) return null;

    // 1. Unified Regex for: 
    // - Mentions (@[Name])
    // - Bug Code References (#APP-101)
    // - Internal Bug URLs (https://.../bugs/:mongoId)
    // - General URLs (https://...)
    const regex = /(@\[[^\]]+\]|#[A-Z0-9]{2,8}-\d{1,6}|https?:\/\/[^\s]+)/g;

    const parts = text.split(regex);

    const result = [];
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      if (!part) continue;

      // Mentions (@[Name])
      if (part.startsWith('@[') && part.endsWith(']')) {
        const name = part.slice(2, -1);
        result.push(
          <span key={`m-${i}`} className="font-bold text-[#0052CC] bg-[#DEEBFF] px-1 py-0.5 rounded text-xs mx-0.5 whitespace-nowrap">
            @{name}
          </span>
        );
        continue;
      }

      // Bug Code Reference (#APP-123)
      if (part.startsWith('#') && /^[A-Z0-9]{2,8}-\d{1,6}$/.test(part.substring(1))) {
        const bugId = part.substring(1);
        result.push(
          <Link
            key={`b-${i}`}
            to={`/bugs/${bugId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="font-mono font-bold text-[#0052CC] hover:underline bg-[#E9F2FF] px-1 rounded mx-0.5 transition-colors"
            title={`View Bug ${bugId}`}
          >
            #{bugId}
          </Link>
        );
        continue;
      }

      // URL check
      if (part.startsWith('http')) {
        // Check if it's an internal bug URL
        const bugUrlRegex = /https?:\/\/[^\s]+?\/bugs\/([a-f0-9]{24}|[A-Z0-9]{2,8}-\d{1,6})/;
        const match = part.match(bugUrlRegex);

        if (match) {
          const idOrCode = match[1];
          const isMongoId = /^[a-f0-9]{24}$/.test(idOrCode);

          if (isMongoId) {
            result.push(
              <SmartBugLink
                key={`sb-${i}`}
                mongoId={idOrCode}
                originalUrl={part}
              />
            );
          } else {
            // It's a code-based URL (e.g., BUG-234)
            result.push(
              <Link
                key={`bl-${i}`}
                to={`/bugs/${idOrCode}`}
                target="_blank"
                rel="noopener noreferrer"
                className="font-bold text-[#0052CC] hover:underline bg-[#E9F2FF] px-1 rounded mx-0.5 transition-colors inline-flex items-center gap-1"
                title={`View Bug ${idOrCode}`}
              >
                <LinkIcon className="h-3.5 w-3.5" />
                {idOrCode}
              </Link>
            );
          }
          continue;
        }

        // General URL
        result.push(
          <a
            key={`u-${i}`}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#0052CC] hover:underline break-all"
          >
            {part}
          </a>
        );
        continue;
      }

      // Plain text — split on newlines to preserve line breaks typed in the textarea
      const lines = part.split('\n');
      lines.forEach((line, lineIdx) => {
        if (line) result.push(<span key={`t-${i}-${lineIdx}`}>{line}</span>);
        // Insert a <br/> after every line except the very last one
        if (lineIdx < lines.length - 1) result.push(<br key={`br-${i}-${lineIdx}`} />);
      });
    }

    return result;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0052CC]"></div>
      </div>
    );
  }

  if (!bug) return null;

  const mediaAttachments = (bug.attachments || [])
    .map((attachment, index) => ({ attachment, index, kind: getMediaKind(attachment) }))
    .filter(m => m.kind);

  const parsed = parseDescriptionIntoSections(bug.description);
  // Display logic:
  // - If the description contains recognizable section headings, show only the parsed DESCRIPTION
  //   so we don't render Steps/Expected/Actual twice (once inside Description and once in sections).
  // - Otherwise, show full description as-is.
  const displayDescription = parsed.hasHeadings ? (parsed.description || '') : (bug.description || '');

  const displaySteps = bug.stepsToReproduce || parsed.steps;
  const displayExpected = bug.expectedResult || parsed.expected;
  const displayActual = bug.actualResult || parsed.actual;

  const canUpdateStatus = user.role === 'developer' || user.role === 'tester';

  return (
    <div className="min-h-screen bg-white font-sans text-[#172B4D]">
      {/* Close Bug Modal — shown when tester selects 'Closed' status */}
      {showCloseBugModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center" role="dialog" aria-modal="true" aria-labelledby="close-bug-modal-title">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setShowCloseBugModal(false)}
          />
          {/* Modal Card */}
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            {/* Header */}
            <div className="px-6 pt-6 pb-4 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-100">
                  <CheckCircleIcon className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <h2 id="close-bug-modal-title" className="text-base font-bold text-[#172B4D]">{pendingVerificationStatus === 'Reopen' ? `Reopen ${bug.ticketType || 'Bug'}` : `Close ${bug.ticketType || 'Bug'}`}</h2>
                  <p className="text-xs text-[#6B778C] mt-0.5">{pendingVerificationStatus === 'Reopen' ? `Provide details for reopening ${(bug.ticketType || 'Bug').toLowerCase()} (optional)` : `Provide verification details for ${(bug.ticketType || 'Bug').toLowerCase()} (optional)`}</p>
                </div>
              </div>
            </div>
            {/* Body */}
            <div className="px-6 py-5 space-y-4">
              <div>
                <label htmlFor="close-bug-verification-link" className="block text-xs font-bold text-[#42526E] uppercase tracking-wider mb-1.5">
                  Verification Link
                </label>
                <input
                  id="close-bug-verification-link"
                  type="url"
                  value={closeBugVerificationLink}
                  onChange={(e) => setCloseBugVerificationLink(e.target.value)}
                  placeholder="Enter link to screenshot, video, or test evidence (optional)"
                  className="w-full px-3 py-2 text-sm border border-[#DFE1E6] rounded-md focus:outline-none focus:ring-2 focus:ring-[#0052CC]/40 focus:border-[#0052CC] transition-all placeholder:text-[#6B778C] bg-[#FAFBFC] focus:bg-white"
                />
              </div>
              <div>
                <label htmlFor="close-bug-verification-notes" className="block text-xs font-bold text-[#42526E] uppercase tracking-wider mb-1.5">
                  Verification Notes
                </label>
                <textarea
                  id="close-bug-verification-notes"
                  rows={3}
                  value={closeBugVerificationNotes}
                  onChange={(e) => setCloseBugVerificationNotes(e.target.value)}
                  placeholder={pendingVerificationStatus === 'Reopen' ? '👉 Describe why the issue still persists' : `Describe how the ${(bug.ticketType || 'Bug').toLowerCase()} fix was verified (optional)`}
                  className="w-full px-3 py-2 text-sm border border-[#DFE1E6] rounded-md focus:outline-none focus:ring-2 focus:ring-[#0052CC]/40 focus:border-[#0052CC] transition-all placeholder:text-[#6B778C] bg-[#FAFBFC] focus:bg-white resize-none"
                />
              </div>
            </div>
            {/* Footer */}
            <div className="px-6 pb-6 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowCloseBugModal(false)}
                className="px-4 py-2 text-sm font-bold text-[#42526E] bg-[#EBECF0] hover:bg-[#DFE1E6] rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCloseBugSubmit}
                className="px-5 py-2 text-sm font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-md transition-colors shadow-sm"
              >
                {pendingVerificationStatus === 'Reopen' ? `Reopen ${bug.ticketType || 'Bug'}` : `Close ${bug.ticketType || 'Bug'}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Developer Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowAssignModal(false)} />
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-sm font-bold text-[#172B4D]">Assign Developer</h2>
              <button onClick={() => setShowAssignModal(false)} className="text-gray-400 hover:text-gray-600 transition-colors">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="px-2 py-3 max-h-[400px] overflow-y-auto">
              {appDevelopers.length > 0 ? (
                <div className="space-y-1">
                  {appDevelopers.map((dev) => (
                    <button
                      key={dev._id}
                      onClick={() => handleAssignDeveloper(dev._id)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-blue-50 transition-all group text-left ${bug.assignedTo?._id === dev._id ? 'bg-blue-50/50 ring-1 ring-blue-100' : ''}`}
                    >
                      <div className={`h-8 w-8 rounded-full flex items-center justify-center font-bold text-xs ring-2 ring-white shadow-sm ${dev.role === 'manager' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                        {dev.name.charAt(0)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-[#172B4D] truncate group-hover:text-blue-700">{dev.name}</p>
                        <p className="text-[10px] text-gray-400 uppercase tracking-wide">{dev.role}</p>
                      </div>
                      {bug.assignedTo?._id === dev._id && <CheckIcon className="h-4 w-4 text-blue-500" />}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="py-10 text-center">
                  <UserGroupIcon className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                  <p className="text-xs text-gray-400">No developers found for this application.</p>
                </div>
              )}
            </div>
            <div className="px-6 py-3 border-t border-gray-100 flex justify-end">
              <button onClick={() => setShowAssignModal(false)} className="text-xs font-semibold text-gray-500 hover:text-gray-700">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* JIRA HEADER SECTION */}
      <div className="border-b border-[#DFE1E6] bg-white sticky top-0 z-30">
        <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-4">
          <div className="flex items-start gap-4">
            {/* Direct Back Button */}
            <button
              onClick={() => {
                const state = JSON.parse(sessionStorage.getItem('bugListState') || '{}');
                const hasHistory = window.history.length > 1;
                if (hasHistory) {
                  navigate(-1);
                } else {
                  const params = new URLSearchParams();
                  if (state.currentPage) params.set('page', String(state.currentPage));
                  if (state.pageSize) params.set('pageSize', String(state.pageSize));
                  if (state.searchTerm) params.set('search', String(state.searchTerm));
                  const filters = state.filters || {};
                  Object.keys(filters).forEach(k => {
                    const v = filters[k];
                    if (Array.isArray(v)) v.forEach(x => x && params.append(k, x));
                    else if (v) params.set(k, String(v));
                  });
                  navigate(`/bugs?${params.toString()}`);
                }
              }}
              className="mt-1 flex items-center justify-center p-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors cursor-pointer shrink-0"
              title="Navigate Back to List"
            >
              <ArrowLeftIcon className="h-5 w-5" />
            </button>

            <div className="flex-1 min-w-0">
              {/* Breadcrumbs */}
              <nav className="flex items-center text-[10px] font-bold text-[#6B778C] mb-1 gap-2">
                <Link
                  to={user?.role === 'tester' ? '/tester/applications' : '/applications'}
                  className="hover:text-[#0052CC] transition-colors uppercase tracking-wider"
                >
                  Applications
                </Link>
                <span className="text-[#DFE1E6]">/</span>
                <Link to={`/bugs?application=${bug.application?._id || bug.application}`} className="hover:text-[#0052CC] transition-colors uppercase tracking-wider">
                  {bug.applicationName || 'APP'}
                </Link>
                <span className="text-[#DFE1E6]">/</span>
                <span className="text-[#0052CC] font-mono tracking-tighter">{bug.bugId}</span>
              </nav>

              <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                <div className="flex-1">
                  <h1 className="text-[26px] font-semibold text-[#172B4D] leading-tight break-words">
                    {bug.title}
                  </h1>
                  {bug.ticketType && bug.ticketType !== 'Bug' && (
                    <span className={`inline-flex items-center mt-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${bug.ticketType === 'Observation' ? 'bg-amber-100 text-amber-800 border-amber-200' :
                        bug.ticketType === 'Feature' ? 'bg-blue-100 text-blue-800 border-blue-200' :
                          bug.ticketType === 'Improvement' ? 'bg-green-100 text-green-800 border-green-200' :
                            'bg-red-100 text-red-800 border-red-200'
                      }`}>
                      {bug.ticketType}
                    </span>
                  )}
                </div>

                {/* Toolbar Actions */}
                <div className="flex items-center gap-2">
                  {user.role === 'tester' && (
                    <Link
                      to={`/bugs/${bug._id}/edit`}
                      className="inline-flex items-center px-4 py-2 bg-[#EBECF0] rounded text-sm font-bold text-[#42526E] hover:bg-[#DFE1E6] transition-all"
                    >
                      Edit
                    </Link>
                  )}
                  <button
                    onClick={() => document.getElementById('comments-section')?.scrollIntoView({ behavior: 'smooth' })}
                    className="inline-flex items-center px-4 py-2 bg-white border border-[#DFE1E6] rounded text-sm font-bold text-[#42526E] hover:bg-[#FAFBFC] transition-all"
                  >
                    <ChatBubbleLeftIcon className="h-4 w-4 mr-2 text-[#6B778C]" />
                    Comment
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">

          {/* MAIN COLUMN */}
          <div className="lg:col-span-8 space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-700">

            {/* Description Section */}
            <section>
              <h2 className="text-[11px] font-bold text-[#172B4D] uppercase tracking-wider mb-3">DESCRIPTION</h2>
              {displayDescription ? (
                <div
                  className="text-[#172B4D] text-sm leading-relaxed compact-html-content"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(displayDescription) }}
                />
              ) : (
                <div className="text-[#172B4D] text-sm leading-relaxed">
                  <span className="text-[#6B778C] italic">No description provided.</span>
                </div>
              )}
            </section>


            {/* Detailed Info (Jira Style Sections) */}
            <section className="space-y-5">
              {!!displaySteps && (
                <div>
                  <h3 className="text-[11px] font-bold text-[#172B4D] uppercase tracking-wider mb-2">
                    • STEPS TO REPRODUCE
                  </h3>
                  <div className="text-sm text-[#172B4D] pl-4 border-l-2 border-[#0052CC]">
                    <div
                      className="leading-relaxed compact-html-content"
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(displaySteps) }}
                    />
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {!!displayExpected && (
                  <div>
                    <h3 className="text-[11px] font-bold text-[#172B4D] uppercase tracking-wider mb-2">EXPECTED RESULT</h3>
                    <div className="text-sm text-[#172B4D] px-3 py-2 border-l-2 border-[#36B37E] bg-[#FAFBFC] rounded-r leading-relaxed compact-html-content">
                      <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(displayExpected) }} />
                    </div>
                  </div>
                )}
                {!!displayActual && (
                  <div>
                    <h3 className="text-[11px] font-bold text-[#172B4D] uppercase tracking-wider mb-2">ACTUAL RESULT</h3>
                    <div className="text-sm text-[#172B4D] px-3 py-2 border-l-2 border-[#DE350B] bg-[#FAFBFC] rounded-r leading-relaxed compact-html-content">
                      <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(displayActual) }} />
                    </div>
                  </div>
                )}
              </div>

              {/* Inline Details (moved to sidebar) */}

              {bug.environment && (
                <div>
                  <h3 className="text-[11px] font-bold text-[#172B4D] uppercase tracking-wider mb-2">ENVIRONMENT</h3>
                  <div className="text-sm text-[#42526E] font-mono bg-transparent border-l-2 border-[#DFE1E6] pl-3 py-1 overflow-x-auto">
                    {bug.environment}
                  </div>
                </div>
              )}
            </section>

            {/* Proof of Fix Verification */}
            {(bug.proofUrl || bug.proofDescription) && (
              <section className="bg-[#E9F2FF] border border-[#B3D4FF] rounded p-6 relative overflow-hidden group">
                <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
                  <CheckCircleIcon className="h-24 w-24 text-[#0052CC]" />
                </div>
                <h2 className="text-lg font-bold text-[#0052CC] mb-4 flex items-center">
                  {bug.status === 'Reopen' ? 'Reopen Justification' : 'Verification Evidence'}
                </h2>

                <div className="space-y-4 relative z-10">
                  {bug.proofUrl && getMediaKind(bug.proofUrl) && (
                    <div className="flex flex-col gap-1">
                      <span className="text-[10px] font-bold text-[#42526E] uppercase tracking-tighter">Evidence</span>
                      <MediaViewer src={getAttachmentUrl(bug.proofUrl)} title="Evidence" className="h-[50vh]" />
                    </div>
                  )}
                  {bug.proofUrl && !getMediaKind(bug.proofUrl) && (
                    <div className="flex flex-col gap-1">
                      <span className="text-[10px] font-bold text-[#42526E] uppercase tracking-tighter">Evidence Link</span>
                      <a
                        href={bug.proofUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#0052CC] hover:text-[#0747A6] font-semibold flex items-center group/link text-sm"
                      >
                        <PaperClipIcon className="h-4 w-4 mr-1.5 group-hover/link:rotate-12 transition-transform" />
                        <span className="border-b border-[#B3D4FF] group-hover/link:border-[#0052CC] transition-colors">View Evidence</span>
                      </a>
                    </div>
                  )}
                  {bug.proofDescription && (
                    <div className="flex flex-col gap-1">
                      <span className="text-[10px] font-bold text-[#42526E] uppercase tracking-tighter">Tester Notes</span>
                      <p className="text-[#172B4D] text-sm italic leading-relaxed bg-white/50 p-3 rounded border border-white/80">
                        "{bug.proofDescription}"
                      </p>
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* Media Preview: images and videos open right here, no new tab */}
            {mediaAttachments.length > 0 && (() => {
              const active = mediaAttachments[Math.min(activeMediaIndex, mediaAttachments.length - 1)];
              return (
                <section ref={mediaViewerRef} className="pt-6 border-t border-[#F4F5F7] scroll-mt-4">
                  <div className="flex items-center justify-between mb-3 gap-3">
                    <h2 className="text-[11px] font-bold text-[#42526E] uppercase tracking-widest">Media ({mediaAttachments.length})</h2>
                    <span className="text-xs text-[#6B778C] truncate">{active.attachment.title || active.attachment.filename}</span>
                  </div>
                  <MediaViewer src={getAttachmentUrl(active.attachment.url)} kind={active.kind} title={active.attachment.title} />
                  {mediaAttachments.length > 1 && (
                    <div className="flex gap-2 mt-3 overflow-x-auto pb-1">
                      {mediaAttachments.map((m, i) => (
                        <button
                          key={m.index}
                          type="button"
                          onClick={() => setActiveMediaIndex(i)}
                          title={m.attachment.title || m.attachment.filename}
                          className={`relative flex-shrink-0 h-16 w-24 rounded overflow-hidden border-2 bg-[#F4F5F7] ${m === active ? 'border-[#0052CC]' : 'border-transparent hover:border-[#B3D4FF]'}`}
                        >
                          {m.kind === 'image' ? (
                            <img src={getAttachmentUrl(m.attachment.url)} alt="" className="h-full w-full object-cover" loading="lazy" />
                          ) : (
                            <>
                              <video src={`${getAttachmentUrl(m.attachment.url)}#t=0.5`} preload="metadata" muted className="h-full w-full object-cover bg-black" />
                              <span className="absolute inset-0 flex items-center justify-center text-white text-lg drop-shadow">▶</span>
                            </>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </section>
              );
            })()}

            {/* Attachments and People Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-6 border-t border-[#F4F5F7]">
              {/* Attachments Section */}
              <section>
                <h2 className="text-[11px] font-bold text-[#42526E] uppercase tracking-widest mb-4">Attachments ({bug.attachments?.length || 0})</h2>
                {bug.attachments && bug.attachments.length > 0 ? (
                  <div className="space-y-2">
                    {bug.attachments.map((attachment, index) => (
                      <div key={index} className="flex items-center group px-2 py-1.5 border border-[#DFE1E6] rounded hover:bg-[#FAFBFC] transition-all">
                        <div className="h-7 w-7 flex-shrink-0 bg-[#EBECF0] rounded flex items-center justify-center text-[#6B778C] mr-2">
                          <PaperClipIcon className="h-4 w-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          {getMediaKind(attachment) ? (
                            <button
                              type="button"
                              onClick={() => {
                                setActiveMediaIndex(mediaAttachments.findIndex(m => m.index === index));
                                mediaViewerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                              }}
                              className="text-sm font-semibold text-[#0052CC] hover:underline truncate block text-left w-full"
                            >
                              {attachment.title || 'Attachment_' + (index + 1)}
                            </button>
                          ) : (
                            <a
                              href={getAttachmentUrl(attachment.url)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm font-semibold text-[#0052CC] hover:underline truncate block"
                            >
                              {attachment.title || 'Attachment_' + (index + 1)}
                            </a>
                          )}
                          <p className="text-[10px] text-[#6B778C] truncate">{attachment.description || attachment.url}</p>
                        </div>
                      </div>
                    ))}
                </div>
                ) : (
                  <p className="text-sm text-[#6B778C] italic">No attachments</p>
                )}
              </section>

              {/* Quick Details (Moved next to Attachments) */}
              <section>
                <h2 className="text-[11px] font-bold text-[#42526E] uppercase tracking-widest mb-4">Details</h2>
                <div className="bg-[#FAFBFC] border border-[#DFE1E6] rounded p-4">
                  <div className="grid grid-cols-2 gap-x-4 gap-y-4">
                    <div className="flex flex-col gap-1">
                      <span className="text-[10px] font-bold text-[#6B778C] uppercase tracking-tighter">Severity</span>
                      <span className={`px-2 py-0.5 rounded font-bold text-[10px] uppercase inline-block w-fit shadow-sm ${severityColors[bug.severity] || 'bg-gray-100 text-gray-800'}`}>
                        {bug.severity}
                      </span>
                    </div>
                    <div className="flex flex-col gap-1">
                      <span className="text-[10px] font-bold text-[#6B778C] uppercase tracking-tighter">Priority</span>
                      <span className={`px-2 py-0.5 rounded font-bold text-[10px] uppercase inline-block w-fit shadow-sm ${priorityColors[bug.priority] || 'bg-gray-100 text-gray-800'}`}>
                        {bug.priority || '—'}
                      </span>
                    </div>
                    <div className="flex flex-col gap-1">
                      <span className="text-[10px] font-bold text-[#6B778C] uppercase tracking-tighter">Module</span>
                      <span className="text-xs font-bold text-[#172B4D] truncate" title={bug.module}>{bug.module || 'None'}</span>
                    </div>
                    <div className="flex flex-col gap-1">
                      <span className="text-[10px] font-bold text-[#6B778C] uppercase tracking-tighter">Category</span>
                      <span className="text-xs font-bold text-[#172B4D]">{bug.category || 'Bug'}</span>
                    </div>
                    {(bug.domain?.name || (typeof bug.domain === 'string' && bug.domain) || bug.ticketType) && (
                      <div className="col-span-2 grid grid-cols-2 gap-x-4 pt-1 border-t border-gray-100 mt-1">
                        {(bug.domain?.name || (typeof bug.domain === 'string' && bug.domain)) ? (
                          <div className="flex flex-col gap-1">
                            <span className="text-[10px] font-bold text-[#6B778C] uppercase tracking-tighter">Domain</span>
                            <span className="px-2 py-0.5 rounded font-bold text-[10px] inline-block w-fit bg-indigo-50 text-indigo-800 border border-indigo-200 shadow-sm">
                              {bug.domain?.name || bug.domain}
                            </span>
                          </div>
                        ) : <div />}
                        {bug.ticketType ? (
                          <div className="flex flex-col gap-1">
                            <span className="text-[10px] font-bold text-[#6B778C] uppercase tracking-tighter">Ticket Type</span>
                            <span className={`px-2 py-0.5 rounded font-bold text-[10px] uppercase inline-block w-fit border shadow-sm ${bug.ticketType === 'Observation' ? 'bg-amber-100 text-amber-800 border-amber-200' :
                                bug.ticketType === 'Feature' ? 'bg-blue-100 text-blue-800 border-blue-200' :
                                  bug.ticketType === 'Improvement' ? 'bg-green-100 text-green-800 border-green-200' :
                                    'bg-red-100 text-red-800 border-red-200'
                              }`}>
                              {bug.ticketType}
                            </span>
                          </div>
                        ) : <div />}
                      </div>
                    )}
                  </div>
                </div>
              </section>
            </div>

            {/* Discussion/Comments */}
            <div id="comments-section" className="space-y-6 pt-8 border-t border-[#F4F5F7]">
              <h2 className="text-sm font-bold text-[#172B4D]">Discussion</h2>
              <div className="space-y-6">
                <h3 className="text-sm font-bold text-[#42526E] flex items-center">
                  Discussion ({bug.comments?.length || 0})
                </h3>

                {/* Add Comment */}
                <div className="flex gap-3">
                  <div className="h-8 w-8 rounded-full bg-[#0052CC] flex-shrink-0 flex items-center justify-center text-white font-bold text-[10px]">
                    {user.name?.charAt(0)}
                  </div>
                  <form onSubmit={handleAddComment} className="flex-1 space-y-2 relative">
                    <textarea
                      ref={commentInputRef}
                      value={comment}
                      onChange={handleCommentChange}
                      onKeyDown={(e) => {
                        if (showSuggestions && filteredSuggestions.length > 0) {
                          // Mention/bug picker navigation — keep unchanged
                          if (e.key === 'ArrowDown') {
                            e.preventDefault();
                            setSelectedIndex(prev => (prev + 1) % filteredSuggestions.length);
                          } else if (e.key === 'ArrowUp') {
                            e.preventDefault();
                            setSelectedIndex(prev => (prev - 1 + filteredSuggestions.length) % filteredSuggestions.length);
                          } else if (e.key === 'Enter' || e.key === 'Tab') {
                            e.preventDefault();
                            insertMention(filteredSuggestions[selectedIndex]);
                          } else if (e.key === 'Escape') {
                            e.preventDefault();
                            setShowSuggestions(false);
                          }
                        } else if (e.key === 'Enter' && !e.shiftKey) {
                          // Enter submits — Shift+Enter adds a new line
                          e.preventDefault();
                          handleAddComment(e);
                        }
                      }}
                      rows="3"
                      className="w-full px-4 py-2 text-sm border-2 border-[#DFE1E6] rounded focus:border-[#0052CC] outline-none transition-all placeholder:text-[#6B778C] bg-[#FAFBFC] focus:bg-white resize-none"
                      placeholder={`Add a comment... (Enter to save · Shift+Enter for new line · @ for people · # for bugs)`}
                    />
                    {/* Hidden mirror for calculating cursor position */}
                    <div
                      ref={mirrorRef}
                      className="absolute top-0 left-0 -z-10 opacity-0 pointer-events-none whitespace-pre-wrap break-words border-2 border-transparent"
                      style={{ visibility: 'hidden', height: '0', overflow: 'hidden' }}
                      aria-hidden="true"
                    ></div>

                    {showSuggestions && filteredSuggestions.length > 0 && (
                      <div
                        className="absolute bg-white border border-[#DFE1E6] rounded-md shadow-[0_8px_24px_-4px_rgba(9,30,66,0.25),0_0_1px_rgba(9,30,66,0.31)] z-[100] overflow-hidden min-w-[320px] animate-in fade-in zoom-in-95 duration-100"
                        style={{
                          top: `${mentionPosition.top + 4}px`,
                          left: `${mentionPosition.left}px`,
                        }}
                      >
                        <div className="bg-[#FAFBFC] px-4 py-2.5 border-b border-[#DFE1E6] flex items-center justify-between">
                          <span className="text-[10px] font-extrabold text-[#42526E] uppercase tracking-widest">
                            {suggestionType === 'user' ? 'Suggested People' : 'Suggested Issues'}
                          </span>
                          <span className="text-[9px] text-[#6B778C] font-semibold bg-[#EBECF0] px-1.5 py-0.5 rounded shadow-sm">
                            ↑↓ to navigate · ↵ to select
                          </span>
                        </div>
                        <div className="max-h-[380px] overflow-y-auto custom-scrollbar bg-white">
                          {filteredSuggestions.map((item, idx) => {
                            const isSelected = idx === selectedIndex;
                            const isBug = suggestionType === 'bug';

                            return (
                              <button
                                key={item._id}
                                type="button"
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  insertMention(item);
                                }}
                                onMouseEnter={() => setSelectedIndex(idx)}
                                className={`w-full text-left px-4 py-3 text-sm flex items-center gap-3 transition-all relative ${isSelected
                                    ? 'bg-[#EBF3FF]'
                                    : 'hover:bg-[#F4F5F7]'
                                  }`}
                              >
                                {isSelected && (
                                  <div className="absolute left-0 top-1 bottom-1 w-[3px] bg-[#0052CC] rounded-r" />
                                )}

                                {isBug ? (
                                  <div className={`h-8 w-8 rounded flex items-center justify-center text-white flex-shrink-0 shadow-sm ${statusIndicatorColors[item.status] ? '' : 'bg-[#42526E]'
                                    }`} style={{ backgroundColor: statusIndicatorColors[item.status] }}>
                                    <span className="text-[10px] font-black italic">!</span>
                                  </div>
                                ) : (
                                  <div className={`h-8 w-8 rounded-full flex items-center justify-center text-[12px] font-black flex-shrink-0 shadow-[inset_0_0_0_1px_rgba(0,0,0,0.05)] ${item.role === 'developer' ? 'bg-[#EBECF0] text-[#42526E]' : 'bg-[#E3FCEF] text-[#006644]'
                                    }`}>
                                    {item.name.charAt(0)}
                                  </div>
                                )}

                                <div className="flex flex-col min-w-0 flex-1">
                                  <div className="flex items-center justify-between gap-3 mb-0.5">
                                    <span className={`truncate text-sm font-bold ${isSelected ? 'text-[#0052CC]' : 'text-[#172B4D]'}`}>
                                      {isBug ? item.bugId : item.name}
                                    </span>
                                    {isBug && (
                                      <span className={`text-[9px] font-black px-2 py-0.5 rounded-full whitespace-nowrap uppercase tracking-tighter ${statusColors[item.status] || 'bg-gray-100'}`}>
                                        {displayStatus(item.status)}
                                      </span>
                                    )}
                                  </div>
                                  <span className="text-[11px] text-[#5E6C84] truncate leading-tight font-medium">
                                    {isBug ? item.title : (item.role || 'Member')}
                                  </span>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    <div className="flex justify-end pt-2">
                      <button
                        type="submit"
                        disabled={!comment.trim() || updating}
                        className={`px-6 py-1.5 bg-[#0052CC] hover:bg-[#0065FF] disabled:bg-[#EBECF0] disabled:text-[#6B778C] disabled:cursor-not-allowed text-white text-sm font-bold rounded transition-all ${updating ? 'opacity-50 blur-[0.5px]' : ''}`}
                      >
                        Save
                      </button>
                    </div>
                  </form>
                </div>


                {/* Comments List */}
                <div className="space-y-6 mt-6">
                  {bug.comments && bug.comments.length > 0 ? (
                    [...bug.comments].reverse().map((c) => (
                      <div
                        key={c._id}
                        id={`comment-${c._id}`}
                        className={`flex gap-3 group rounded transition-colors ${highlightCommentId === c._id ? 'bg-[#DEEBFF] ring-2 ring-[#2684FF]/40 p-2 -m-2' : ''}`}
                      >
                        <div className={`h-8 w-8 rounded-full flex-shrink-0 flex items-center justify-center font-bold text-[10px] ${c.user?.role === 'developer' ? 'bg-[#EBECF0] text-[#42526E]' : 'bg-[#E3FCEF] text-[#006644]'
                          }`}>
                          {c.user?.name?.charAt(0)}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-bold text-[#172B4D]">{c.user?.name}</span>
                            <span className="text-[10px] text-[#6B778C]">
                              {format(new Date(c.createdAt), 'MMM d, h:mm a')}
                            </span>
                          </div>
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1">
                              {editingComment === c._id ? (
                                <textarea
                                  value={editedCommentText}
                                  onChange={(e) => setEditedCommentText(e.target.value)}
                                  rows="2"
                                  className="w-full px-3 py-2 text-sm border border-[#DFE1E6] rounded focus:border-[#0052CC] outline-none transition-all resize-none"
                                  autoFocus
                                />
                              ) : (
                                <p className="text-sm text-[#172B4D] leading-relaxed whitespace-pre-line">{renderCommentText(c.text)}</p>
                              )}
                            </div>
                            {c.user?._id === user._id && (
                              <div className="flex items-center gap-3 transition-opacity flex-shrink-0 comment-actions">
                                {editingComment === c._id ? (
                                  <div className="flex items-center gap-2">
                                    <button
                                      onClick={() => handleSaveEdit(c._id)}
                                      className="text-xs text-green-600 hover:text-green-800 font-medium"
                                    >
                                      Save
                                    </button>
                                    <button
                                      onClick={handleCancelEdit}
                                      className="text-xs text-gray-600 hover:text-gray-800 font-medium"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-2">
                                    <button
                                      onClick={() => handleEditComment(c._id, c.text)}
                                      className="flex items-center gap-1 text-[#6B7280] hover:text-[#111827] text-sm transition-colors"
                                      title="Edit comment"
                                    >
                                      <PencilIcon className="h-4 w-4" />
                                      <span className="text-xs">Edit</span>
                                    </button>
                                    <button
                                      onClick={() => handleDeleteComment(c._id)}
                                      className="flex items-center gap-1 text-[#6B7280] hover:text-[#111827] text-sm transition-colors"
                                      title="Delete comment"
                                    >
                                      <TrashIcon className="h-4 w-4" />
                                      <span className="text-xs">Delete</span>
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          
                          {/* Replies Display Area */}
                          {c.replies && c.replies.length > 0 && (
                            <div className="mt-4 border-l-2 border-[#DFE1E6] space-y-4">
                              {c.replies.map((reply) => (
                                <div key={reply._id} className="flex gap-3 relative ml-4 group">
                                  {/* Visual connector */}
                                  <div className="absolute -left-4 top-4 w-3 border-t-2 border-[#DFE1E6]" />
                                  <div className={`h-6 w-6 rounded-full flex-shrink-0 flex items-center justify-center font-bold text-[10px] ${reply.user?.role === 'developer' ? 'bg-[#EBECF0] text-[#42526E]' : 'bg-[#E3FCEF] text-[#006644]'}`}>
                                    {reply.user?.name?.charAt(0)}
                                  </div>
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-0.5">
                                      <span className="text-xs font-bold text-[#172B4D]">{reply.user?.name}</span>
                                      <span className="text-[10px] text-[#6B778C]">
                                        {format(new Date(reply.createdAt), 'MMM d, h:mm a')}
                                      </span>
                                    </div>
                                    <div className="flex items-start justify-between gap-2">
                                      <div className="flex-1">
                                        {editingReplyId === reply._id ? (
                                          <textarea
                                            value={editedReplyText}
                                            onChange={(e) => setEditedReplyText(e.target.value)}
                                            rows="2"
                                            className="w-full px-3 py-1.5 text-xs border border-[#DFE1E6] rounded focus:border-[#0052CC] outline-none transition-all resize-none mt-1"
                                            autoFocus
                                          />
                                        ) : (
                                          <p className="text-[13px] text-[#172B4D] leading-relaxed whitespace-pre-line">{renderCommentText(reply.text)}</p>
                                        )}
                                      </div>
                                      
                                      {reply.user?._id === user._id && (
                                        <div className="flex items-center gap-3 transition-opacity flex-shrink-0 comment-actions ml-2">
                                          {editingReplyId === reply._id ? (
                                            <div className="flex items-center gap-2">
                                              <button onClick={() => handleSaveEditReply(c._id, reply._id)} className="text-[10px] text-green-600 hover:text-green-800 font-medium tracking-wide">Save</button>
                                              <button onClick={handleCancelEditReply} className="text-[10px] text-gray-600 hover:text-gray-800 font-medium tracking-wide">Cancel</button>
                                            </div>
                                          ) : (
                                            <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                              <button onClick={() => handleEditReply(reply._id, reply.text)} className="flex items-center gap-1 text-[#6B7280] hover:text-[#111827] text-xs transition-colors" title="Edit reply">
                                                <PencilIcon className="h-3 w-3" />
                                              </button>
                                              <button onClick={() => handleDeleteReply(c._id, reply._id)} className="flex items-center gap-1 text-[#6B7280] hover:text-[#111827] text-xs transition-colors" title="Delete reply">
                                                <TrashIcon className="h-3 w-3" />
                                              </button>
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Reply Actions & Input Box */}
                          <div className="mt-2 flex gap-3">
                            <button
                              onClick={() => {
                                setReplyingToComment(replyingToComment === c._id ? null : c._id);
                                setReplyText('');
                                setActiveInputType('reply');
                              }}
                              className="text-[11px] font-bold text-[#6B778C] hover:text-[#0052CC] transition-colors"
                            >
                              Reply
                            </button>
                          </div>

                          {replyingToComment === c._id && (
                            <form onSubmit={(e) => handleAddReply(e, c._id)} className="mt-3 flex gap-2 relative">
                              <textarea
                                ref={replyInputRef}
                                value={replyText}
                                onChange={(e) => handleCommentChange(e, 'reply')}
                                onKeyDown={(e) => {
                                  if (showSuggestions && filteredSuggestions.length > 0) {
                                    // Mention/bug picker navigation — keep unchanged
                                    if (e.key === 'ArrowDown') {
                                      e.preventDefault();
                                      setSelectedIndex(prev => (prev + 1) % filteredSuggestions.length);
                                    } else if (e.key === 'ArrowUp') {
                                      e.preventDefault();
                                      setSelectedIndex(prev => (prev - 1 + filteredSuggestions.length) % filteredSuggestions.length);
                                    } else if (e.key === 'Enter' || e.key === 'Tab') {
                                      e.preventDefault();
                                      insertMention(filteredSuggestions[selectedIndex]);
                                    } else if (e.key === 'Escape') {
                                      e.preventDefault();
                                      setShowSuggestions(false);
                                    }
                                  } else if (e.key === 'Enter' && !e.shiftKey) {
                                    // Enter submits — Shift+Enter adds a new line
                                    e.preventDefault();
                                    handleAddReply(e, c._id);
                                  }
                                }}
                                rows="2"
                                className="w-full px-3 py-1.5 text-xs border border-[#DFE1E6] rounded focus:border-[#0052CC] outline-none transition-all placeholder:text-[#6B778C] bg-[#FAFBFC] focus:bg-white resize-none"
                                placeholder={`Reply to ${c.user?.name}... (Enter to send · Shift+Enter for new line · @ to mention)`}
                                autoFocus
                              />
                              <button
                                type="submit"
                                disabled={!replyText.trim() || updating}
                                className={`px-4 py-1.5 bg-[#0052CC] hover:bg-[#0065FF] disabled:bg-[#EBECF0] disabled:text-[#6B778C] flex-shrink-0 text-white text-xs font-bold rounded transition-all disabled:cursor-not-allowed ${updating ? 'opacity-50 blur-[0.5px]' : ''}`}
                              >
                                Reply
                              </button>
                            </form>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-4 bg-[#FAFBFC] rounded border border-dashed border-[#DFE1E6]">
                      <p className="text-sm text-[#6B778C]">No comments yet.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* SIDEBAR COLUMN */}
          <div className="lg:col-span-4 space-y-6 animate-in fade-in slide-in-from-right duration-700">

            {/* Status (compact) */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-[11px] font-bold text-[#6B778C] uppercase tracking-widest">Status</h3>
                {updating && <span className="animate-spin h-3 w-3 border-2 border-[#0052CC] border-t-transparent rounded-full font-normal"></span>}
              </div>
              {canUpdateStatus ? (
                <div className="w-[180px] space-y-2">
                  {StatusDropdown()}
                </div>
              ) : (
                <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded font-bold text-xs uppercase ${statusColors[bug.status] || 'bg-gray-100 text-gray-800'}`}>
                  <span>{displayStatus(bug.status)}</span>
                  <span className="text-[10px] opacity-70">▼</span>
                </div>
              )}
            </div>

            {/* People References */}
            <div className="pt-2">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-[11px] font-bold text-[#42526E] uppercase tracking-widest">People References</h2>
                {(user.role === 'developer' || user.role === 'manager') && (
                  <button
                    onClick={() => setShowAssignModal(true)}
                    className="flex items-center gap-1.5 px-2.5 py-1 bg-white border border-[#DFE1E6] rounded shadow-sm hover:border-[#0052CC] hover:text-[#0052CC] transition-all text-[10px] font-bold text-[#42526E] group"
                  >
                    <UserPlusIcon className="h-3.5 w-3.5 group-hover:scale-110 transition-transform" />
                    Assign
                  </button>
                )}
              </div>
              <div className="bg-[#FAFBFC] border border-[#DFE1E6] rounded p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-[#6B778C] uppercase tracking-tighter">Reporter</span>
                  <div className="flex items-center gap-2.5">
                    <div className="h-7 w-7 rounded-full bg-[#E3FCEF] text-[#006644] flex items-center justify-center text-[11px] font-black shadow-sm ring-1 ring-white">
                      {bug.reportedBy?.name?.charAt(0) || 'U'}
                    </div>
                    <span className="text-sm font-semibold text-[#172B4D]">{bug.reportedBy?.name || 'Unknown'}</span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-[#6B778C] uppercase tracking-tighter">Assignee</span>
                  <div className="flex items-center gap-2.5">
                    <div className="h-7 w-7 rounded-full bg-[#EBECF0] text-[#42526E] flex items-center justify-center text-[11px] font-black shadow-sm ring-1 ring-white">
                      {bug.assignedTo?.name?.charAt(0) || '?'}
                    </div>
                    <span className="text-sm font-semibold text-[#172B4D]">{bug.assignedTo?.name || 'Unassigned'}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Timeline (no card/container) */}
            <div className="flex flex-col h-[80vh] min-h-[400px] pt-4 border-t border-[#F4F5F7]">
              <div className="flex items-center justify-between mb-4 flex-shrink-0 bg-white">
                <h3 className="text-[11px] font-bold text-[#6B778C] uppercase tracking-widest">Activity Timeline</h3>
              </div>
              <div className="flex-1 overflow-y-auto pr-2 pb-4 custom-scrollbar">
                <UnifiedTimeline bugs={[bug]} hideTitle={true} />
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default BugDetail;
